import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLessonApi, type RunMode } from '../../hooks/useLessonApi';
import { useLessonState } from '../../hooks/useLessonState';
import { useAutoContinue } from '../../hooks/useAutoContinue';
import { useLessonFlow } from '../../hooks/useLessonFlow';
import { useLadderAudio } from '../../hooks/useLadderAudio';
import TurnCardView from '../turncard/TurnCardView';
import { useRunDebrief } from '../../hooks/useRunDebrief';
import type { WordConfidence } from '../../speech';
import { fetchPhraseAudio } from '../../utils/stepHandlers';
import { toPracticeSummary, type PracticeSummary } from '../../api/practice';

// Extracted helper functions for testability
// These allow us to test defensive branches independently

// Guard function to check if already started
export const checkAlreadyStarted = (
  hasStartedRef: React.MutableRefObject<boolean>
): boolean => {
  return hasStartedRef.current;
};

// Guard function to check if already advancing
export const checkAlreadyAdvancing = (
  isAdvancingRef: React.MutableRefObject<boolean>
): boolean => {
  return isAdvancingRef.current;
};

// Wrapper for handleNextStep with error handling
export const callHandleNextStepWithErrorHandling = async (
  handleNextStepFn: (runId: string, stepVersion: number) => Promise<unknown>,
  runId: string,
  stepVersion: number,
  isAdvancingRef: React.MutableRefObject<boolean>
): Promise<void> => {
  try {
    await handleNextStepFn(runId, stepVersion);
  } catch (err) {
    console.error('[startLesson] Error in handleNextStep:', err);
    isAdvancingRef.current = false; // Reset on error
  }
};

// Check if abort controller exists and abort it
export const abortPreviousRequest = (
  abortControllerRef: React.MutableRefObject<AbortController | null>
): void => {
  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }
};

interface LessonRunnerProps {
  /** `practice` runs a practice session (`/practice/...`) instead of a lesson. */
  mode?: RunMode;
}

export const LessonRunner: React.FC<LessonRunnerProps> = ({
  mode = 'lesson',
}) => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const state = useLessonState();
  const lessonApi = useLessonApi(mode);
  const isPractice = mode === 'practice';
  const [practiceSummary, setPracticeSummary] =
    React.useState<PracticeSummary | null>(null);
  // In practice mode, keep the counts the final `/next` reports.
  const api = isPractice
    ? {
        ...lessonApi,
        nextStep: async (
          ...args: Parameters<typeof lessonApi.nextStep>
        ): ReturnType<typeof lessonApi.nextStep> => {
          const result = await lessonApi.nextStep(...args);
          if (result.completed) {
            setPracticeSummary(toPracticeSummary(result.assessment));
          }
          return result;
        },
      }
    : lessonApi;
  const autoContinue = useAutoContinue();
  const {
    startLesson,
    resumeOffer,
    resumed,
    continueRun,
    restartRun,
    handleNextStep,
    abortControllerRef,
  } = useLessonFlow(lessonId, state, api, autoContinue, mode);

  useEffect(() => {
    // Reset state when lessonId changes
    // Cancel any in-flight requests from previous mount
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    state.reset();
    setPracticeSummary(null);

    // Create new AbortController for this effect run
    abortControllerRef.current = new AbortController();
    const controller = abortControllerRef.current;

    startLesson(controller.signal);

    // Cleanup: abort request if component unmounts or lessonId changes
    return (): void => {
      controller.abort();
      abortControllerRef.current = null;
      // Reset ref on cleanup so next mount can start fresh
      state.hasStartedRef.current = false;
    };
    // Note: Intentionally only depend on lessonId to reset on route change
  }, [lessonId]);

  const handleStartRecording = (): void => {
    // MicButton handles recording start internally
    // This callback can be used for analytics or UI state if needed
    console.log('[LessonRunner] Recording started');
  };

  const handleStopRecording = (): void => {
    // MicButton handles recording stop internally
    // The final transcript will be passed to handleTranscript
    console.log('[LessonRunner] Recording stopped');
  };

  // Keep a ref in sync with phraseHints state so async callbacks read a fresh value
  const phraseHintsRef = React.useRef<string[] | null>(state.phraseHints);
  phraseHintsRef.current = state.phraseHints;

  // Receives the final transcript from the STT provider (via MicButton) and
  // submits it for evaluation. Transcription itself now happens inside the
  // provider, so this no longer touches audio.
  const handleTranscript = React.useCallback(
    async (result: {
      transcript: string;
      confidence: number | null;
      words?: WordConfidence[];
    }): Promise<void> => {
      // Only process if we have runId and stepVersion
      if (!state.runId || !state.stepVersion) {
        console.warn(
          '[LessonRunner] Cannot process transcript: missing runId or stepVersion'
        );
        return;
      }

      const hints = phraseHintsRef.current;

      try {
        state.setProcessing(true);
        state.setPhraseHints(null);
        // The attempt is in; the next step decides whether the mic comes back.
        state.setAwaitingInput(false);

        // #204 P1: show the learner's transcribed speech as a message so
        // the exchange (including retries) stays visible in the conversation.
        state.addMessage({
          id: crypto.randomUUID(),
          sender: 'user',
          content: result.transcript,
          timestamp: new Date().toISOString(),
          ...(result.words ? { words: result.words } : {}),
        });

        // Send the transcript to the backend for evaluation (give_feedback).
        await handleNextStep(state.runId, state.stepVersion, {
          transcript: result.transcript,
          confidence: result.confidence,
        });
      } catch (err) {
        console.error('[LessonRunner] Error processing transcript:', err);
        // Restore hints so the next retry attempt still benefits from them
        state.setPhraseHints(hints);
        state.setError(
          err instanceof Error
            ? err.message
            : 'Failed to process audio. Please try again.'
        );
      } finally {
        state.setProcessing(false);
      }
    },
    [state.runId, state.stepVersion, handleNextStep, state]
  );

  // Surface speech-recognition failures. Both 'aborted' (stopped with nothing
  // recognized) and 'no-speech' are
  // soft user-recoverable conditions that must NOT call state.setError — doing
  // so replaces the entire lesson with a full-page ErrorBanner (unmounting
  // MicButton) and causes a lesson restart when the user taps Retry. Instead,
  // MicButton already returns to idle after finishTurn(); the learner simply
  // taps the mic again (after a missed turn, a card invites them to). Permission and other hard errors show the provider's
  // message. The record button is re-enabled automatically because MicButton
  // resets after each turn.
  const handleSttError = React.useCallback(
    (error: { code: string; message: string }): void => {
      if (error.code === 'no-speech' || error.code === 'aborted') {
        // BR-011: no response keeps the learner on this step and invites another try.
        state.addMessage({
          id: crypto.randomUUID(),
          sender: 'tutor',
          content: "I didn't catch that — tap the mic and try again.",
          timestamp: new Date().toISOString(),
          stepKind: 'no_speech',
        });
        return;
      }
      if (error.code === 'mic-unavailable') {
        // MicButton shows a Reload button in place of the mic (#254).
        return;
      }
      state.setError(error.message);
    },
    [state]
  );

  const handleRetry = (): void => {
    // Bypass guard for retry to allow retrying after errors
    startLesson(undefined, true);
  };

  // Phrases whose advance has started (synchronous guard against double taps);
  // advancedPhraseId mirrors the latest one for rendering.
  const advancedPhrasesRef = React.useRef<Set<string>>(new Set());
  const [advancedPhraseId, setAdvancedPhraseId] = React.useState<string | null>(
    null
  );

  // The phrase the lesson is waiting on: the newest message, when it is a
  // play_audio step the lesson hasn't advanced past. Replaying an earlier
  // phrase, or a feedback replay clip, must not advance the lesson.
  const latestMessage = state.messages[state.messages.length - 1];
  const currentPhraseId =
    latestMessage?.id.includes('-play_audio-') &&
    latestMessage.id !== advancedPhraseId
      ? latestMessage.id
      : null;

  // Advances past the current phrase once its clip has been heard, or when
  // the learner skips a phrase whose clip can't be played (#249). The mic is
  // not touched here; it follows state.awaitingInput (set from the step the
  // advance returns).
  const advancePastPhrase = React.useCallback(
    (messageId: string) => {
      if (!state.runId || !state.stepVersion) {
        return;
      }
      if (messageId !== currentPhraseId) {
        return;
      }
      if (advancedPhrasesRef.current.has(messageId)) {
        return;
      }
      advancedPhrasesRef.current.add(messageId);
      setAdvancedPhraseId(messageId);

      console.log('[LessonRunner] Advancing past phrase', messageId);
      void handleNextStep(state.runId, state.stepVersion).then((advanced) => {
        // The advance failed (its error is handled inside the flow): offer
        // the phrase again so replaying or skipping retries (#244).
        if (!advanced) {
          advancedPhrasesRef.current.delete(messageId);
          setAdvancedPhraseId(null);
        }
      });
    },
    [state.runId, state.stepVersion, currentPhraseId, handleNextStep]
  );

  // Re-runs an automatic advance that failed (#250). The failed call already
  // moved state.stepVersion to the new step, so this repeats the same request.
  const handleRetryAdvance = React.useCallback(() => {
    if (!state.runId || !state.stepVersion) {
      return;
    }
    void handleNextStep(state.runId, state.stepVersion);
  }, [state.runId, state.stepVersion, handleNextStep]);

  // Re-fetches the clips of a phrase whose audio failed to load. On success
  // the phrase gets its player back and listen-first resumes.
  const handleRetryAudio = React.useCallback(
    async (messageId: string): Promise<boolean> => {
      const formId = state.messages.find(
        (msg) => msg.id === messageId
      )?.audioFormId;
      if (!formId) return false;
      try {
        const audioUrls = await fetchPhraseAudio(
          formId,
          api,
          abortControllerRef.current?.signal
        );
        state.setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? { ...msg, audioUrls, audioUnavailable: undefined }
              : msg
          )
        );
        return true;
      } catch (error) {
        console.error('[LessonRunner] Audio retry failed:', error);
        return false;
      }
    },
    [state.messages, state.setMessages, api, abortControllerRef]
  );

  // The clip a help ladder offers (BR-051) is fetched when the ladder arrives.
  useLadderAudio({
    messages: state.messages,
    setMessages: state.setMessages,
    api,
  });

  // "?" on a persona line (BR-043): what did the character just say?
  const handleAskHelp = React.useCallback(
    (formId: string | null) => {
      if (!state.runId) return Promise.reject(new Error('No active run'));
      return lessonApi.askHelp(state.runId, formId);
    },
    [state.runId]
  );

  // "I need help" (BR-052): the next level of help for the phrase, without a failed attempt. Lessons and practice.
  const handleNeedHelp = React.useCallback(() => {
    if (!state.runId || !state.stepVersion) {
      return;
    }
    state.setAwaitingInput(false);
    void handleNextStep(state.runId, state.stepVersion, {
      transcript: '',
      confidence: null,
      askHelp: true,
    });
  }, [state.runId, state.stepVersion, handleNextStep, state.setAwaitingInput]);

  const { debrief, loading: debriefLoading } = useRunDebrief(
    state.runId,
    !isPractice && state.lessonComplete
  );

  return (
    <TurnCardView
      mode={isPractice ? 'practice' : 'lesson'}
      loading={state.loading}
      processing={state.processing}
      error={state.error}
      messages={state.messages}
      earlierPhrases={state.earlierPhrases}
      parts={state.parts}
      contextName={state.contextName}
      lessonComplete={state.lessonComplete}
      loadingMessage={isPractice ? 'Starting practice...' : undefined}
      onRetry={handleRetry}
      onBackFromError={() => navigate('/')}
      onLeave={() => navigate('/')}
      onStartRecording={handleStartRecording}
      onStopRecording={handleStopRecording}
      onTranscript={handleTranscript}
      onSttError={handleSttError}
      languageCode={state.languageCode ?? undefined}
      phraseHints={state.phraseHints}
      runId={state.runId}
      stepIndex={state.stepIndex}
      onAudioEnded={advancePastPhrase}
      currentPhraseId={currentPhraseId}
      onRetryAudio={handleRetryAudio}
      onSkipAudio={advancePastPhrase}
      onAskHelp={handleAskHelp}
      awaitingInput={state.awaitingInput}
      advanceFailed={state.advanceFailed}
      onRetryAdvance={handleRetryAdvance}
      onNeedHelp={handleNeedHelp}
      resumeOffer={resumeOffer}
      resumed={resumed}
      onContinue={() => void continueRun(abortControllerRef.current?.signal)}
      onRestart={() => void restartRun(abortControllerRef.current?.signal)}
      debrief={debrief}
      debriefLoading={debriefLoading}
      practiceSummary={practiceSummary}
      onStartCheckIn={
        state.contextId
          ? () => navigate(`/assessment/${state.contextId}`)
          : undefined
      }
    />
  );
};

export default LessonRunner;
